
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle } from "lucide-react";
import { useEffect, useState } from "react";

interface ProcessingErrorAlertProps {
  isOpen: boolean;
  onClose: () => void;
  errorDetails?: string;
  errorCategory?: string;
  userGuidance?: string;
}

export const ProcessingErrorAlert = ({
  isOpen,
  onClose,
  errorDetails,
  errorCategory,
  userGuidance,
}: ProcessingErrorAlertProps) => {
  // Add state to control actual visibility with a delay to prevent flickering
  const [isVisible, setIsVisible] = useState(false);
  
  // Control visibility with a delay to prevent flickering
  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null;
    
    if (isOpen) {
      // Add a small delay before showing to prevent flickering
      timeoutId = setTimeout(() => {
        setIsVisible(true);
      }, 300);
    } else {
      setIsVisible(false);
    }
    
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isOpen]);
  
  // Enhanced format error details function that handles various error types
  const formatErrorDetails = (details?: string) => {
    if (!details) return "No error details available";
    
    try {
      // Try to parse if it's a JSON string
      const parsed = typeof details === 'string' ? JSON.parse(details) : details;
      
      // If it's an object or array, format it nicely
      if (typeof parsed === 'object') {
        // Handle Excel processing errors
        if (parsed.name === 'ExcelProcessingError') {
          const causeInfo = parsed.cause ? ` (${parsed.cause.message || parsed.cause.name})` : '';
          return `Excel processing error: ${parsed.message}${causeInfo}`;
        }
        
        // Handle specific error types
        if (parsed.error === 'insufficient_examples') {
          return `You need at least 10 examples for fine-tuning. Currently you have ${parsed.currentCount || 0} examples.`;
        }
        
        if (parsed.error === 'validation_error' || parsed.code === 'validation_error') {
          return `Training data validation error: ${parsed.message || 'Your training data format is invalid for OpenAI fine-tuning.'}`;
        }
        
        if (parsed.error === 'file_processing_error' || parsed.code === 'file_processing_error') {
          return `File processing error: ${parsed.message || 'The Excel file could not be processed.'}`;
        }
        
        if (parsed.name === 'FunctionsHttpError' || parsed.name === 'NetworkError') {
          return `Network error communicating with server. Please try again.`;
        }
        
        // Handle various error formats
        if (parsed.message) {
          return parsed.message;
        }
        if (parsed.error && parsed.error.message) {
          return parsed.error.message;
        }
        if (parsed.details && parsed.details.message) {
          return parsed.details.message;
        }
        
        // Handle other object formats - pretty print JSON
        return JSON.stringify(parsed, null, 2);
      }
      
      return details;
    } catch {
      // If not valid JSON, return as is
      return details;
    }
  };
  
  // Enhanced error analysis with fail-fast approach and specific guidance
  const analyzeError = (details?: string) => {
    if (!details) return {
      message: "An unknown error occurred during processing",
      guidance: "Processing has been stopped to prevent data corruption. Please refresh the page and try again.",
      category: "Unknown Error"
    };
    
    const lowerDetails = details.toLowerCase();
    
    try {
      const parsed = typeof details === 'string' ? JSON.parse(details) : details;
      
      if (typeof parsed === 'object') {
        // Check for enhanced error objects from our new system
        if (parsed.category && parsed.guidance) {
          return {
            message: parsed.message || "Processing error occurred",
            guidance: parsed.guidance,
            category: parsed.category,
            technicalDetails: parsed.technicalDetails
          };
        }
        
        // Handle edge function errors (500 status)
        if (parsed.status === 500 || parsed.name === 'FunctionsHttpError') {
          return {
            message: "AI processing service encountered an internal error",
            guidance: "Processing has been stopped to prevent data corruption. The AI service may be experiencing issues. Please try again in a few minutes or contact support if this persists.",
            category: "AI Service Error"
          };
        }
        
        // Handle legacy error formats
        if (parsed.name === 'ExcelProcessingError') {
          const causeInfo = parsed.cause ? ` (${parsed.cause.message || parsed.cause.name})` : '';
          return {
            message: `Excel processing error: ${parsed.message}${causeInfo}`,
            guidance: "Processing has been stopped. Please check your Excel file format and try again.",
            category: "File Processing Error"
          };
        }
        
        if (parsed.error === 'insufficient_examples') {
          return {
            message: `You need at least 10 examples for fine-tuning. Currently you have ${parsed.currentCount || 0} examples.`,
            guidance: "Processing cannot continue. Add more training examples to your dataset and try again.",
            category: "Training Data Error"
          };
        }
        
        // Extract message from various formats
        const message = parsed.message || parsed.error?.message || parsed.details || 'Processing error';
        return {
          message: typeof message === 'string' ? message : JSON.stringify(message),
          guidance: "Processing has been stopped. Please try again or contact support if the issue persists.",
          category: parsed.category || "Processing Error"
        };
      }
      
      // Enhanced string-based error analysis
      if (lowerDetails.includes('500') || lowerDetails.includes('internal server error')) {
        return {
          message: "AI processing service encountered an internal error",
          guidance: "Processing has been stopped to prevent data corruption. Please try again or contact support if this persists.",
          category: "AI Service Error"
        };
      }
      
      if (lowerDetails.includes('timeout') || lowerDetails.includes('timed out')) {
        return {
          message: "Processing operation timed out",
          guidance: "Processing has been stopped. Try processing fewer items at once, use a smaller file, or check your internet connection.",
          category: "Timeout Error"
        };
      }
      
      if (lowerDetails.includes('network') || lowerDetails.includes('connection')) {
        return {
          message: "Network connection issue occurred",
          guidance: "Connection lost during processing. Processing has been stopped. Check your internet connection and try again.",
          category: "Network Error"
        };
      }
      
      if (lowerDetails.includes('duplicate') || lowerDetails.includes('already processing')) {
        return {
          message: "Processing already in progress",
          guidance: "This collection is already being processed by another session. Please wait for it to complete or refresh the page.",
          category: "Duplicate Processing"
        };
      }
      
      if (lowerDetails.includes('rate limit') || lowerDetails.includes('quota')) {
        return {
          message: "API rate limit or quota exceeded",
          guidance: "You have reached your usage limits. Processing has been stopped. Wait a few minutes or check your subscription limits.",
          category: "Rate Limit Exceeded"
        };
      }
      
      return {
        message: typeof details === 'string' ? details : 'Processing error',
        guidance: "Processing has been stopped. Please try again or contact support if the issue persists.",
        category: "Processing Error"
      };
    } catch {
      return {
        message: typeof details === 'string' && details.length < 100 ? details : "An error occurred during processing",
        guidance: "Processing has been stopped to prevent data corruption. Please refresh the page and try again.",
        category: "Processing Error"
      };
    }
  };
  
  const errorAnalysis = analyzeError(errorDetails);

  if (!isVisible) return null;
  
  return (
    <AlertDialog open={isVisible} onOpenChange={(open) => {
      if (!open) onClose();
    }}>
      <AlertDialogContent className="max-w-lg">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            {errorCategory || errorAnalysis.category}
          </AlertDialogTitle>
          <AlertDialogDescription>
            <div className="space-y-4">
              <p className="font-medium">{errorAnalysis.message}</p>
              
              <div className="p-3 bg-muted rounded-md">
                <p className="text-sm font-medium text-primary mb-2">What to do:</p>
                <p className="text-sm">{userGuidance || errorAnalysis.guidance}</p>
              </div>
              
              {errorAnalysis.technicalDetails && (
                <details className="text-sm">
                  <summary className="cursor-pointer font-medium text-primary">Technical Details</summary>
                  <div className="mt-2 p-2 bg-muted rounded font-mono text-xs overflow-auto max-h-40">
                    {errorAnalysis.technicalDetails}
                  </div>
                </details>
              )}
              
              {errorDetails && !errorAnalysis.technicalDetails && (
                <details className="text-sm">
                  <summary className="cursor-pointer font-medium text-primary">Error Details</summary>
                  <div className="mt-2 p-2 bg-muted rounded font-mono text-xs overflow-auto max-h-40">
                    {formatErrorDetails(errorDetails)}
                  </div>
                </details>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction onClick={onClose}>Close</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
